/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    ContentContainer,
    DisplayHtmlContent,
    Heading,
    NeedToKnow,
    Stack,
    Text,
} from 'sarsaparilla';

import { OrderDetailsLayout } from 'shared-ui/components/OrderDetailsLayout';
import PassSelectionError from '../containers/PassSelectionError';
import SitePassPageHeader from './SitePassPageHeader/SitePassPageHeader';
import ReservationSummary from './ReservationSummary';
import DetailSummary from './DetailSummary';
import PassInfoForm from './PassInfoForm';
import NoFeePeriods from './parkPasses/NoFeePeriods';
import LoadingSkeleton from './loadingSkeleton/LoadingSkeleton';
import { FacilityProps, PassTypeProps, ReservationProps } from '../props';
import { formatPrice } from '../utils';

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
function PassOrderDetails({
    facility = {},
    sitePassImage,
    reservation = null,
    passTypes = [],
    passType = null,
    submitAttempted = false,
    isLoading = true,
    isNewReservation = false,
    isInWidget = false, // used for widget
    onNeedToKnowChange = () => {},
    onReservationChange = () => {},
    onDiscountBlur = () => {},
    onRemoveReservation = () => {},
    onSaveReservation = () => {},
    renderValidationErrors = () => {}, // used for widget
    parentSelector, // used for widget
}) {
    const isNeedToKnowChecked = useMemo(() => {
        return !!reservation?.pass_info?.features?.accepted?.need_to_know;
    }, [reservation?.pass_info?.features?.accepted?.need_to_know]);
    const needToKnowIntroText = useMemo(() => {
        return `Please carefully review the important information below and click the check box to agree to the rules and regulations. The information in this section only pertains to this pass. Other important visitation policies and details can be found on the ${process.env.SITE_NAME} page for ${facility?.name}.`;
    }, [facility?.name]);
    const showNonResidentFees = useMemo(() => {
        return passTypes?.some((pt) => pt?.features?.non_resident_fee);
    }, [passTypes]);

    const handlePassInfoChange = useCallback(
        (passTypeId, passInfo, isValid) => {
            if (reservation && passType) {
                // Default "discount" feature.
                const discount = {};
                const accepted = {};
                if (passType && passType.features.accepted) {
                    accepted.accepted = passType.features.accepted.reduce((memo, key) => {
                        const value =
                            reservation?.pass_info?.features?.accepted?.[key] ?? false;

                        return {
                            ...memo,
                            [key]: value,
                        };
                    }, {});
                }

                // Build updated reservation.
                const updatedReservation = {
                    ...reservation,
                    pass_info: passInfo,

                    // New API updates:
                    pass_type_id: passTypeId || reservation.pass_type_id,
                    holder_info: passInfo.holder_info,
                    features: {
                        discount,
                        ...passInfo.features,
                        ...accepted,
                    },
                };

                onReservationChange(updatedReservation, isValid);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onReservationChange, passType, reservation]
    );

    const handleCancelButtonClick = () => {
        window.location = '/cart';
        return null;
    };

    const reservationSummary = (
        <ReservationSummary
            isLoading={isLoading}
            heading={passType?.name}
            imageUrl={sitePassImage}
            parentName={facility?.name}
            passType={passType}
            reservation={reservation}
            facility={facility}
            proceedToCartButtonText={!isNewReservation ? 'Save Changes' : 'Add to Cart'}
            secondaryButtonText={!isNewReservation ? 'Cancel Changes' : undefined}
            isDrawerWrapFullWidth
            onProceedToCartClick={onSaveReservation}
            onSecondaryButtonClick={
                !isNewReservation ? handleCancelButtonClick : undefined
            }
        >
            <DetailSummary
                facilityName={facility?.name}
                passType={passType}
                reservation={reservation}
            />
        </ReservationSummary>
    );

    const renderNeedToKnow = () => {
        const needToKnow = facility?.contents?.need_to_know ?? [];

        return (
            <NeedToKnow
                introText={needToKnowIntroText}
                isChecked={isNeedToKnowChecked}
                isInvalid={!isNeedToKnowChecked && submitAttempted}
                onChange={onNeedToKnowChange}
            >
                {needToKnow.map(({ title, paragraphs = [] }, i) => (
                    <section className="rec-order-detail-need-help" key={`section-${i}`}>
                        {!!title && (
                            <Heading className="mb-1" headingLevel={3} appearance="h6">
                                {title}
                            </Heading>
                        )}
                        {paragraphs.map((html, n) => (
                            <DisplayHtmlContent
                                key={`html-${n}`}
                                html={`<p>${html}</p>`}
                            />
                        ))}
                    </section>
                ))}

                <NoFeePeriods
                    siteName={facility?.name}
                    noFeePeriods={facility?.free_days}
                    agency={facility?.agency}
                />
            </NeedToKnow>
        );
    };

    if (isLoading) {
        return <LoadingSkeleton />;
    }

    if (!reservation || !passType) {
        return null;
    }

    const renderValidationErrorsComponent = () => {
        if (isInWidget) {
            return renderValidationErrors();
        }

        return (
            <PassSelectionError
                reservations={[reservation]}
                submitAttempted={submitAttempted}
                passTypes={passTypes}
                needToKnowChecked={isNeedToKnowChecked}
            />
        );
    };

    return (
        <ContentContainer className="pass-order-details">
            <OrderDetailsLayout summaryElement={reservationSummary}>
                <Stack space="lg">
                    <SitePassPageHeader
                        isLoading={false}
                        heading="Site Pass Order Details"
                        facilityName={facility?.name}
                        isInWidget={isInWidget}
                    />
                    <Box>
                        <Stack>
                            <Box>
                                <Heading
                                    className="mb-1"
                                    headingLevel={3}
                                    appearance="h5"
                                >
                                    {!isNewReservation ? 'Modify' : 'Pass Details'}
                                </Heading>
                                <Text>
                                    {!isNewReservation
                                        ? 'Please modify the pass information below:'
                                        : 'Please provide the required information below:'}
                                </Text>
                            </Box>
                            {renderValidationErrorsComponent()}
                            <div className="rec-internal-reservation-details-parkpass">
                                <PassInfoForm
                                    submitAttempted={submitAttempted}
                                    facility={facility}
                                    number={1}
                                    passInfo={reservation?.pass_info}
                                    passType={passType}
                                    price={formatPrice(reservation?.price)}
                                    renderRemove
                                    isPending={reservation?.is_pending}
                                    reservationId={reservation?.id}
                                    onChange={handlePassInfoChange}
                                    onDiscountBlur={onDiscountBlur}
                                    onRemove={onRemoveReservation}
                                    parentSelector={parentSelector}
                                    showNonResidentFees={showNonResidentFees}
                                />
                            </div>
                        </Stack>
                    </Box>
                    {renderNeedToKnow()}
                </Stack>
            </OrderDetailsLayout>
        </ContentContainer>
    );
}

PassOrderDetails.propTypes = {
    facility: FacilityProps,
    sitePassImage: PropTypes.string,
    reservation: ReservationProps,
    passTypes: PropTypes.arrayOf(PassTypeProps),
    passType: PassTypeProps,
    submitAttempted: PropTypes.bool,
    isLoading: PropTypes.bool,
    isNewReservation: PropTypes.bool,
    isInWidget: PropTypes.bool,
    onNeedToKnowChange: PropTypes.func,
    onReservationChange: PropTypes.func,
    onDiscountBlur: PropTypes.func,
    onRemoveReservation: PropTypes.func,
    onSaveReservation: PropTypes.func,
    renderValidationErrors: PropTypes.func,
    /** Used by ui-widget for the Modal to change the parent element that the modal will be attached to */
    parentSelector: PropTypes.func,
};

export default PassOrderDetails;
